import SparklesIcon from '../shared/SparklesIcon';
import type { Photo } from '../../types';

export type AutoTagState = {
    open: boolean;
    running: boolean;
    done: boolean;
    total: number;
    currentIndex: number;
    currentPhoto: Photo | undefined;
    currentStage: string;
    tagCloud: Map<string, number>;
    processed: number;
    skipped: number;
    groupCount: number;
};

interface Props {
    state: AutoTagState;
    showCancelConfirm: boolean;
    onCancelClick: () => void;
    onCancelConfirm: () => void;
    onCancelDeny: () => void;
    onClose: () => void;
}

export default function AutoTagModal({ state, showCancelConfirm, onCancelClick, onCancelConfirm, onCancelDeny, onClose }: Props) {
    const { running, done, total, currentIndex, currentPhoto, currentStage, tagCloud, processed, skipped, groupCount } = state;

    const progress = total > 0 ? Math.round(((processed ?? currentIndex) / total) * 100) : 0;
    const tagEntries = tagCloud ? [...tagCloud.entries()].sort((a, b) => b[1] - a[1]) : [];
    const countLabel = done
        ? `${processed ?? 0} of ${total} processed`
        : `${(currentIndex ?? 0) + 1} of ${total}`;

    return (
        <div className='ai-modal-backdrop' onClick={e => { if (e.target === e.currentTarget && !running) onClose(); }}>
            <div className='ai-modal' role='dialog' aria-modal='true' aria-label='Auto Tag with AI'>
                <div className='ai-modal-header'>
                    <h2 className='ai-modal-title'>
                        <SparklesIcon size={20} style={{ flexShrink: 0 }} />
                        Auto Tag with AI
                    </h2>
                    {!running && (
                        <button className='ai-modal-close' onClick={onClose} aria-label='Close'>×</button>
                    )}
                </div>

                {currentPhoto && (
                    <div className='ai-modal-current'>
                        <div className='ai-modal-thumb'>
                            <img src={currentPhoto.thumbnailURL ?? currentPhoto.displayURL} alt={currentPhoto.originalFilename} />
                        </div>
                        <div className='ai-modal-photo-info'>
                            <div className='ai-modal-filename'>{currentPhoto.originalFilename}</div>
                            <div className={`ai-modal-stage${done ? ' done' : ''}`}>{currentStage}</div>
                        </div>
                    </div>
                )}

                <div className='ai-modal-progress-row'>
                    <span className='ai-modal-count'>{countLabel}</span>
                </div>
                <div className='ai-modal-progress-bar-track'>
                    <div className='ai-modal-progress-bar-fill' style={{ width: `${done ? 100 : progress}%` }} />
                </div>

                {tagEntries.length > 0 && (
                    <div className='ai-modal-cloud-section'>
                        <div className='ai-modal-cloud-label'>Tags used</div>
                        <div className='ai-modal-cloud'>
                            {tagEntries.map(([name, count]) => (
                                <span key={name} className={`ai-cloud-chip${name === 'Group' ? ' group' : ''}`}>
                                    {name}
                                    <span className='ai-cloud-count'>{count}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {done && (
                    <div className='ai-modal-summary'>
                        <div className='ai-modal-summary-item'>
                            <span className='ai-summary-value'>{processed ?? 0}</span>
                            <span className='ai-summary-label'>photos processed</span>
                        </div>
                        <div className='ai-modal-summary-item'>
                            <span className='ai-summary-value'>{groupCount ?? 0}</span>
                            <span className='ai-summary-label'>marked as group</span>
                        </div>
                        <div className='ai-modal-summary-item'>
                            <span className='ai-summary-value'>{tagEntries.filter(([n]) => n !== 'Group').length}</span>
                            <span className='ai-summary-label'>unique artists tagged</span>
                        </div>
                        {(skipped ?? 0) > 0 && (
                            <div className='ai-modal-summary-item'>
                                <span className='ai-summary-value'>{skipped}</span>
                                <span className='ai-summary-label'>skipped / errors</span>
                            </div>
                        )}
                    </div>
                )}

                {showCancelConfirm && (
                    <div className='ai-cancel-confirm'>
                        <p>Stop processing? Tags saved so far will be kept.</p>
                        <div className='ai-cancel-confirm-btns'>
                            <button className='ai-cancel-deny-btn' onClick={onCancelDeny}>Keep going</button>
                            <button className='ai-cancel-confirm-btn' onClick={onCancelConfirm}>Stop</button>
                        </div>
                    </div>
                )}

                <div className='ai-modal-footer'>
                    {running && !showCancelConfirm && (
                        <button className='ai-modal-cancel-btn' onClick={onCancelClick}>Cancel</button>
                    )}
                    {done && (
                        <button className='ai-modal-done-btn' onClick={onClose}>Done</button>
                    )}
                </div>
            </div>
        </div>
    );
}